/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { GuestCounter, Icon, Button, Label, Stack, Inline, Box } from 'sarsaparilla';
import { equipmentTypes } from '../../utils/utilsNew';
import PereMarquetteInfoModal from '../registration/PereMarquetteInfoModal';
import { PERE_MARQUETTE_ID } from '../../constants/index';

export default class WatercraftAvailabilityFilter extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            modalOpen: false,
        };

        this.onInputChange = this.onInputChange.bind(this);
        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
    }

    onInputChange(equipments) {
        const newQuotaWeight = this.props.equipments.reduce((sum, option) => {
            const weight = option.weight || (option.weight === 0 ? 0 : 1);
            return sum + weight * equipments[equipmentTypes.get(option.type)];
        }, 0);

        const newEquipmentCount = this.props.equipments.reduce((sum, option) => {
            return sum + equipments[equipmentTypes.get(option.type)];
        }, 0);

        const { maxValue, maxEquipmentCount } = this.props;

        let quotaWeightsWithinRange = !maxValue || newQuotaWeight <= maxValue;

        if (this.props.permitID === PERE_MARQUETTE_ID) {
            // Pere Marquette should use flat count of watercraft instead of total quota
            quotaWeightsWithinRange =
                quotaWeightsWithinRange || newEquipmentCount <= maxValue;
        }

        const equipmentCountsWithinRange =
            !maxEquipmentCount || newEquipmentCount <= maxEquipmentCount;

        // make sure we don't go over max
        if (quotaWeightsWithinRange && equipmentCountsWithinRange) {
            const newEquipments = this.props.equipments.map((equip) => ({
                ...equip,
                qty: equipments[equipmentTypes.get(equip.type)],
            }));

            this.props.onChange({
                equipments: newEquipments,
                value: newQuotaWeight,
                count: newEquipmentCount,
            });
        }
    }

    openModal() {
        this.setState({
            modalOpen: true,
        });
    }

    closeModal() {
        this.setState({
            modalOpen: false,
        });
    }

    render() {
        // guest counter expects a type and value of different shapes, that's why we do this:
        const equipments = this.props.equipments.reduce((oldAcc, equip) => {
            const acc = { ...oldAcc };
            acc[equipmentTypes.get(equip.type)] = equip.qty;
            return acc;
        }, {});

        // guest counter expects a type and value of different shapes, that's why we do this:
        const guestTypes = this.props.equipments.map((equip) => ({
            ...equip,
            key: equipmentTypes.get(equip.type),
            type: equipmentTypes.get(equip.type),
        }));

        let watercraftString = 'Watercraft';
        if (this.props.permitID === PERE_MARQUETTE_ID) {
            const equipmentCount = this.props.equipments.reduce((sum, option) => {
                return sum + equipments[equipmentTypes.get(option.type)];
            }, 0);
            if (equipmentCount !== 0) {
                watercraftString = `Quota, ${equipmentCount} Watercraft`;
            }
        }

        const Wraper = this.props.inline ? Inline : Stack;

        return (
            <Wraper space="sm">
                <Box>
                    <Label htmlFor="number-input">
                        {`${this.props.label} (${this.props.maxValue ? this.props.maxValue : this.props.maxEquipmentCount} max)`}
                        <Button
                            type="button"
                            appearance="link"
                            onClick={this.openModal}
                            screenReaderTextBefore="Opens a modal describing how watercraft fee and quota calculation works at this permit location"
                            iconAfterElement={<Icon iconName="info-outline" />}
                        />
                    </Label>
                </Box>
                <Box>
                    <GuestCounter
                        onCountChange={this.onInputChange}
                        value={equipments}
                        title={watercraftString}
                        guestTypes={guestTypes}
                    />
                </Box>
                <PereMarquetteInfoModal
                    isOpen={this.state.modalOpen}
                    closeModal={this.closeModal}
                />
            </Wraper>
        );
    }
}

WatercraftAvailabilityFilter.propTypes = {
    equipments: PropTypes.arrayOf(PropTypes.shape({})),
    maxValue: PropTypes.number,
    maxEquipmentCount: PropTypes.number,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    inline: PropTypes.bool,
};

WatercraftAvailabilityFilter.defaultProps = {
    onChange: () => {},
    inline: false,
    label: '',
    minValue: 0,
    maxValue: 0,
    equipments: [],
};
